import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { Footer } from '../../components/Footer';
import { ENTITIES, NAV, type NavItem } from './data';
import { DocsSeo } from './DocsSeo';
import { AddUserGuide } from './pages/AddUserGuide';
import { ApiIndex } from './pages/ApiIndex';
import { ApiKeysIntegrationGuide } from './pages/ApiKeysIntegrationGuide';
import { BillingScheduleGuide } from './pages/BillingScheduleGuide';
import { BillingTimingGuide } from './pages/BillingTimingGuide';
import { Changelog } from './pages/Changelog';
import { Concept } from './pages/Concept';
import { ContractsGuide } from './pages/ContractsGuide';
import { CreditNotesGuide } from './pages/CreditNotesGuide';
import { CrmGuide } from './pages/CrmGuide';
import { CsmWorkloadGuide } from './pages/CsmWorkloadGuide';
import { CustomFieldsGuide } from './pages/CustomFieldsGuide';
import { DailyCheckinsGuide } from './pages/DailyCheckinsGuide';
import { DocumentLibraryGuide } from './pages/DocumentLibraryGuide';
import { DriveFoldersGuide } from './pages/DriveFoldersGuide';
import { CustomAgreementsGuide } from './pages/CustomAgreementsGuide';
import { CustomizeInvoicesGuide } from './pages/CustomizeInvoicesGuide';
import { DiscountingGuide } from './pages/DiscountingGuide';
import { EventIngestion } from './pages/EventIngestion';
import { Example } from './pages/Example';
import { FormsApi } from './pages/FormsApi';
import { FormsGuide } from './pages/FormsGuide';
import { WorkInboxGuide } from './pages/WorkInboxGuide';
import { ExternalIdGuide } from './pages/ExternalIdGuide';
import { ExternalReferences } from './pages/ExternalReferences';
import { Guides } from './pages/Guides';
import { ImportInvoicesGuide } from './pages/ImportInvoicesGuide';
import { ProductEventsApi } from './pages/ProductEventsApi';
import { ProductUsageGuide } from './pages/ProductUsageGuide';
import { InvoicePaymentsApi } from './pages/InvoicePaymentsApi';
import { LaunchBundleGuide } from './pages/LaunchBundleGuide';
import { LegacyPricingGuide } from './pages/LegacyPricingGuide';
import { LifecycleLogGuide } from './pages/LifecycleLogGuide';
import { McpConnection } from './pages/McpConnection';
import { MeteredUsageGuide } from './pages/MeteredUsageGuide';
import { MidCycleChangesGuide } from './pages/MidCycleChangesGuide';
import { MultiCurrencyGuide } from './pages/MultiCurrencyGuide';
import { HypothesesGuide } from './pages/HypothesesGuide';
import { OkrGuide } from './pages/OkrGuide';
import { Overview } from './pages/Overview';
import { PriceOverrideGuide } from './pages/PriceOverrideGuide';
import { PricingModelsGuide } from './pages/PricingModelsGuide';
import { ProspectFormIntegrationGuide } from './pages/ProspectFormIntegrationGuide';
import { PublicGraphQLApi } from './pages/PublicGraphQLApi';
import { PublicGraphQLIntegrationGuide } from './pages/PublicGraphQLIntegrationGuide';
import { SupportAiGuide } from './pages/SupportAiGuide';
import { SupportGuidelinesGuide } from './pages/SupportGuidelinesGuide';
import { SupportChatApi } from './pages/SupportChatApi';
import { SupportInboxGuide } from './pages/SupportInboxGuide';
import { SupportKnowledgeGuide } from './pages/SupportKnowledgeGuide';
import { SupportKnowledgeApiGuide } from './pages/SupportKnowledgeApiGuide';
import { SupportSetupGuide } from './pages/SupportSetupGuide';
import { SupportTokensGuide } from './pages/SupportTokensGuide';
import { ProductEventsSetupGuide } from './pages/ProductEventsSetupGuide';
import { SupportWidgetGuide } from './pages/SupportWidgetGuide';
import { Quickstart } from './pages/Quickstart';
import { RenewalsGuide } from './pages/RenewalsGuide';
import { RevvlyAgentGuide } from './pages/RevvlyAgentGuide';
import { ScopedPricingGuide } from './pages/ScopedPricingGuide';
import { UsageReportingGuide } from './pages/UsageReportingGuide';
import { VersioningGuide } from './pages/VersioningGuide';
import { WebhookPaymentGuide } from './pages/WebhookPaymentGuide';
import { CollectPaymentsGuide } from './pages/CollectPaymentsGuide';
import { FixYourDataGuide } from './pages/FixYourDataGuide';
import { TasksGuide } from './pages/TasksGuide';
import { WorkspaceSettingsGuide } from './pages/WorkspaceSettingsGuide';
import { TaxGuide } from './pages/TaxGuide';
import { CreateSubscriptionGuide } from './pages/CreateSubscriptionGuide';
import { TeamsSeatsGuide } from './pages/TeamsSeatsGuide';
import { NotificationsGuide } from './pages/NotificationsGuide';
import { OutboundWebhooksGuide } from './pages/OutboundWebhooksGuide';
import { ApiKeyCapabilities } from './pages/ApiKeyCapabilities';
import { McpToolCatalogue } from './pages/McpToolCatalogue';
import { AssistantCapabilities } from './pages/AssistantCapabilities';
import { IntegrationsAvailability } from './pages/IntegrationsAvailability';
import { TermsAndNames } from './pages/TermsAndNames';
import { ExportsReference } from './pages/ExportsReference';
import { WorkspaceFeaturesReference } from './pages/WorkspaceFeaturesReference';
import { AutomaticJobsReference } from './pages/AutomaticJobsReference';
import { RevenueMetricsReference } from './pages/RevenueMetricsReference';
import { LimitsReference } from './pages/LimitsReference';
import { ReferenceIndex } from './pages/ReferenceIndex';

import './styles/api.css';
import './styles/diagrams.css';
import './styles/concept.css';
import './styles/schema.css';
import './styles/shell.css';

const LINK_ITEMS = NAV.filter((n): n is Extract<NavItem, { type: 'link' }> => n.type === 'link');
const GROUP_ITEMS = NAV.filter((n): n is Extract<NavItem, { type: 'group' }> => n.type === 'group');

/** The API / developer reference pages, grouped under a single "API" menu. */
const API_LINKS: { path: string; label: string; blurb: string }[] = [
  {
    path: 'api',
    label: 'All endpoints',
    blurb: 'Every documented public endpoint with deep links',
  },
  {
    path: 'api/events',
    label: 'Event Ingestion API',
    blurb: 'Stream metered usage events — REST + HMAC signing',
  },
  {
    path: 'api/product-events',
    label: 'Product Events API',
    blurb: 'Send product usage events and erase a user’s',
  },
  {
    path: 'api/invoice-payments',
    label: 'Invoice Payment Webhook',
    blurb: 'Mark invoices paid — in full or in part',
  },
  {
    path: 'api/graphql-public',
    label: 'Public GraphQL API',
    blurb: 'Read clients and submit prospects with your API key',
  },
  {
    path: 'api/forms',
    label: 'Forms API',
    blurb: 'Read a published form, upload files and submit answers',
  },
  {
    path: 'api/support-chat',
    label: 'Support chat API',
    blurb: 'Build your own chat widget for signed-in users',
  },
  {
    path: 'api/mcp',
    label: 'MCP connection',
    blurb: 'Connect Claude or MCP clients to live billing data, with human-approved writes',
  },
  {
    path: 'api/external-references',
    label: 'External references',
    blurb: 'Address resources by your own ids',
  },
];

function BrandMark() {
  return (
    <svg viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <g transform="translate(4 6.5)">
        <path
          d="M1.021 10.5625C4.31511 10.5625 5.13864 2.78709 6.7857 1.15015C8.43276 -0.0775511 8.84453 7.6979 10.0798 13.8364C10.9033 17.5195 12.1386 17.9287 13.3739 15.4733C15.021 12.1995 16.2563 5.24249 17.9033 4.42402C19.1386 4.01479 20.3739 8.51636 22.021 8.51636"
          stroke="#082535"
          strokeWidth="2.04195"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
    </svg>
  );
}

function Header() {
  const location = useLocation();
  // One state for both menus so opening one closes the other.
  const [openMenu, setOpenMenu] = useState<'api' | 'concepts' | null>(null);
  const conceptsOpen = openMenu === 'concepts';
  const apiOpen = openMenu === 'api';
  const [activeCluster, setActiveCluster] = useState<string>('catalog');
  const [query, setQuery] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);
  const conceptsWrapRef = useRef<HTMLDivElement>(null);
  const apiWrapRef = useRef<HTMLDivElement>(null);
  const searchWrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (ev: MouseEvent) => {
      const target = ev.target as Node;
      const openWrap =
        openMenu === 'api'
          ? apiWrapRef.current
          : openMenu === 'concepts'
            ? conceptsWrapRef.current
            : null;
      if (openWrap && !openWrap.contains(target)) {
        setOpenMenu(null);
      }
      if (searchWrapRef.current && !searchWrapRef.current.contains(target)) {
        setQuery('');
      }
    };
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') setOpenMenu(null);
    };
    document.addEventListener('click', handler);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', handler);
      document.removeEventListener('keydown', onKey);
    };
  }, [openMenu]);

  useEffect(() => {
    setOpenMenu(null);
    setQuery('');
    setMobileOpen(false);
  }, [location.pathname]);

  const activeGroup = useMemo(() => {
    return (
      GROUP_ITEMS.find((g) => g.label.toLowerCase().startsWith(activeCluster)) ||
      GROUP_ITEMS.find((g) => g.label.toLowerCase().startsWith('catalog')) ||
      GROUP_ITEMS[0]
    );
  }, [activeCluster]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [] as string[];
    return Object.values(ENTITIES)
      .filter((e) => e.cluster !== 'root')
      .filter(
        (e) =>
          e.name.toLowerCase().includes(q) ||
          e.oneLiner.toLowerCase().includes(q) ||
          e.plain.toLowerCase().includes(q),
      )
      .map((e) => e.id);
  }, [query]);

  const isConceptsActive = location.pathname.startsWith('/docs/concepts/');
  const isApiActive = location.pathname.startsWith('/docs/api');

  return (
    <header className="tn-header">
      <Link className="tn-brand" to="/" aria-label="Back to revvly.ai">
        <div className="tn-brand-tile">
          <BrandMark />
        </div>
        <div className="tn-brand-name">Revvly</div>
        <span className="tn-brand-sub">Docs</span>
      </Link>
      <button
        type="button"
        className="tn-mobile-toggle"
        aria-label={mobileOpen ? 'Close navigation' : 'Open navigation'}
        aria-expanded={mobileOpen}
        onClick={() => setMobileOpen((o) => !o)}
      >
        <span className={`tn-burger ${mobileOpen ? 'tn-burger-open' : ''}`} aria-hidden="true">
          <span />
          <span />
          <span />
        </span>
      </button>
      <div className={`tn-panel ${mobileOpen ? 'tn-panel-open' : ''}`}>
        <nav className="tn-links">
          {LINK_ITEMS.map((item) => (
            <div className="tn-link-wrap" key={item.id}>
              <NavLink
                end={item.path === ''}
                to={item.path ? `/docs/${item.path}` : '/docs'}
                className={({ isActive }) => `tn-link ${isActive ? 'tn-active' : ''}`}
              >
                {item.label}
              </NavLink>
            </div>
          ))}
          <div className="tn-link-wrap" ref={apiWrapRef}>
            <button
              className={`tn-link ${isApiActive ? 'tn-active' : ''}`}
              aria-expanded={apiOpen}
              aria-controls="api-dropdown"
              onClick={() => setOpenMenu((m) => (m === 'api' ? null : 'api'))}
            >
              API <span className="tn-chev">▾</span>
            </button>
            {apiOpen && (
              <div id="api-dropdown" className="tn-dropdown">
                {API_LINKS.map((item) => (
                  <Link
                    key={item.path}
                    className="tn-dd-item"
                    to={`/docs/${item.path}`}
                    onClick={() => setOpenMenu(null)}
                  >
                    {item.label}
                    <small>{item.blurb}</small>
                  </Link>
                ))}
              </div>
            )}
          </div>
          <div className="tn-link-wrap" ref={conceptsWrapRef}>
            <button
              className={`tn-link ${isConceptsActive ? 'tn-active' : ''}`}
              aria-expanded={conceptsOpen}
              aria-controls="concepts-dropdown"
              onClick={() => setOpenMenu((m) => (m === 'concepts' ? null : 'concepts'))}
            >
              Concepts <span className="tn-chev">▾</span>
            </button>
            {conceptsOpen && (
              <div id="concepts-dropdown" className="tn-dropdown tn-dd-mega">
                <div className="tn-dd-clusters">
                  {GROUP_ITEMS.map((g) => {
                    const key = g.label.toLowerCase().split(' ')[0];
                    return (
                      <button
                        key={g.label}
                        className={`tn-dd-cluster ${activeCluster === key ? 'on' : ''}`}
                        onMouseEnter={() => setActiveCluster(key)}
                        onClick={() => setActiveCluster(key)}
                      >
                        {g.label}
                        <small>{g.ids.length}</small>
                      </button>
                    );
                  })}
                </div>
                <div className="tn-dd-items">
                  {activeGroup?.ids.map((id) => (
                    <Link
                      key={id}
                      className="tn-dd-item"
                      to={`/docs/concepts/${id}`}
                      onClick={() => setOpenMenu(null)}
                    >
                      {ENTITIES[id].name}
                      <small>{ENTITIES[id].oneLiner}</small>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </nav>
        <div className="tn-link-wrap tn-search" ref={searchWrapRef}>
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.3-4.3" />
          </svg>
          <input
            type="search"
            aria-label="Search documentation"
            placeholder="Search everything…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {query.trim() && (
            <div className="tn-search-results">
              {results.length === 0 ? (
                <div className="tn-sr-empty">No matches for &ldquo;{query}&rdquo;</div>
              ) : (
                results.map((id) => (
                  <Link
                    key={id}
                    className="tn-dd-item"
                    to={`/docs/concepts/${id}`}
                    onClick={() => setQuery('')}
                  >
                    {ENTITIES[id].name}
                    <small>{ENTITIES[id].oneLiner}</small>
                  </Link>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

function Shell() {
  useEffect(() => {
    const body = document.body;
    const html = document.documentElement;
    body.classList.add('docs-body');
    const previousSnap = html.style.scrollSnapType;
    const previousOverflowX = html.style.overflowX;
    html.style.scrollSnapType = 'none';
    html.style.overflowX = 'clip';
    return () => {
      body.classList.remove('docs-body');
      html.style.scrollSnapType = previousSnap;
      html.style.overflowX = previousOverflowX;
    };
  }, []);

  return (
    <div className="tn-root">
      <Header />
      <main className="tn-body">
        <Outlet />
      </main>
      <Footer />
      {/* Rendered after Outlet so its effect (document.title, meta, JSON-LD)
          commits after each page's own `document.title = ...` effect and wins,
          instead of the reverse (the page's title would clobber this one). */}
      <DocsSeo />
    </div>
  );
}

export function DocsLayout() {
  return (
    <Routes>
      <Route element={<Shell />}>
        <Route index element={<Overview />} />
        <Route path="quickstart" element={<Quickstart />} />
        <Route path="example" element={<Example />} />
        <Route path="guides" element={<Guides />} />
        <Route path="assistant" element={<RevvlyAgentGuide />} />
        <Route path="api" element={<ApiIndex />} />
        <Route path="api/events" element={<EventIngestion />} />
        <Route path="api/external-references" element={<ExternalReferences />} />
        <Route path="api/product-events" element={<ProductEventsApi />} />
        <Route path="api/invoice-payments" element={<InvoicePaymentsApi />} />
        <Route path="api/graphql-public" element={<PublicGraphQLApi />} />
        <Route path="api/mcp" element={<McpConnection />} />
        <Route path="api/support-chat" element={<SupportChatApi />} />
        <Route path="api/forms" element={<FormsApi />} />
        <Route path="guides/multi-currency" element={<MultiCurrencyGuide />} />
        <Route path="guides/pricing-models" element={<PricingModelsGuide />} />
        <Route path="guides/scoped-pricing" element={<ScopedPricingGuide />} />
        <Route path="guides/custom-agreements" element={<CustomAgreementsGuide />} />
        <Route path="guides/discounting" element={<DiscountingGuide />} />
        <Route path="guides/override-pricing" element={<PriceOverrideGuide />} />
        <Route path="guides/billing-timing" element={<BillingTimingGuide />} />
        <Route path="guides/billing-schedules" element={<BillingScheduleGuide />} />
        <Route path="guides/credit-notes" element={<CreditNotesGuide />} />
        <Route path="guides/contracts" element={<ContractsGuide />} />
        <Route path="guides/versioning" element={<VersioningGuide />} />
        <Route path="guides/legacy-pricing" element={<LegacyPricingGuide />} />
        <Route path="guides/renewals" element={<RenewalsGuide />} />
        <Route path="guides/launch-bundle" element={<LaunchBundleGuide />} />
        <Route path="guides/mid-cycle-changes" element={<MidCycleChangesGuide />} />
        <Route path="guides/lifecycle-log" element={<LifecycleLogGuide />} />
        <Route path="guides/import-invoices" element={<ImportInvoicesGuide />} />
        <Route path="guides/webhook-payment" element={<WebhookPaymentGuide />} />
        <Route path="guides/external-id" element={<ExternalIdGuide />} />
        <Route path="guides/prospect-form-integration" element={<ProspectFormIntegrationGuide />} />
        <Route path="guides/api-keys" element={<ApiKeysIntegrationGuide />} />
        <Route path="guides/usage-reporting" element={<UsageReportingGuide />} />
        <Route path="guides/metered-usage" element={<MeteredUsageGuide />} />
        <Route path="guides/public-graphql" element={<PublicGraphQLIntegrationGuide />} />
        <Route path="guides/support-inbox" element={<SupportInboxGuide />} />
        <Route path="guides/support-knowledge" element={<SupportKnowledgeGuide />} />
        <Route path="guides/support-ai" element={<SupportAiGuide />} />
        <Route path="guides/support-guidelines" element={<SupportGuidelinesGuide />} />
        <Route path="guides/support-setup" element={<SupportSetupGuide />} />
        <Route path="guides/support-tokens" element={<SupportTokensGuide />} />
        <Route path="guides/support-knowledge-api" element={<SupportKnowledgeApiGuide />} />
        <Route path="guides/product-events-setup" element={<ProductEventsSetupGuide />} />
        <Route path="guides/support-widget" element={<SupportWidgetGuide />} />
        <Route path="guides/add-user" element={<AddUserGuide />} />
        <Route path="guides/crm" element={<CrmGuide />} />
        <Route path="guides/csm-workload" element={<CsmWorkloadGuide />} />
        <Route path="guides/custom-fields" element={<CustomFieldsGuide />} />
        <Route path="guides/document-library" element={<DocumentLibraryGuide />} />
        <Route path="guides/forms" element={<FormsGuide />} />
        <Route path="guides/work-inbox" element={<WorkInboxGuide />} />
        <Route path="guides/daily-checkins" element={<DailyCheckinsGuide />} />
        <Route path="guides/product-usage" element={<ProductUsageGuide />} />
        <Route path="guides/drive-folders" element={<DriveFoldersGuide />} />
        <Route path="guides/okrs" element={<OkrGuide />} />
        <Route path="guides/hypotheses" element={<HypothesesGuide />} />
        <Route path="guides/customize-invoices" element={<CustomizeInvoicesGuide />} />
        <Route path="guides/collect-payments" element={<CollectPaymentsGuide />} />
        <Route path="guides/fix-your-data" element={<FixYourDataGuide />} />
        <Route path="guides/tasks" element={<TasksGuide />} />
        <Route path="guides/workspace-settings" element={<WorkspaceSettingsGuide />} />
        <Route path="guides/tax" element={<TaxGuide />} />
        <Route path="guides/create-subscription" element={<CreateSubscriptionGuide />} />
        <Route path="guides/teams-and-seats" element={<TeamsSeatsGuide />} />
        <Route path="guides/notifications" element={<NotificationsGuide />} />
        <Route path="guides/outbound-webhooks" element={<OutboundWebhooksGuide />} />
        <Route path="reference/api-keys" element={<ApiKeyCapabilities />} />
        <Route path="reference/mcp-tools" element={<McpToolCatalogue />} />
        <Route path="reference/assistant-tools" element={<AssistantCapabilities />} />
        <Route path="reference/integrations" element={<IntegrationsAvailability />} />
        <Route path="reference/terms" element={<TermsAndNames />} />
        <Route path="reference/exports" element={<ExportsReference />} />
        <Route path="reference/features" element={<WorkspaceFeaturesReference />} />
        <Route path="reference/automatic-jobs" element={<AutomaticJobsReference />} />
        <Route path="reference/metrics" element={<RevenueMetricsReference />} />
        <Route path="reference/limits" element={<LimitsReference />} />
        <Route path="reference" element={<ReferenceIndex />} />
        <Route path="changelog" element={<Changelog />} />
        <Route path="concepts/:id" element={<Concept />} />
        <Route path="*" element={<Concept />} />
      </Route>
    </Routes>
  );
}
